import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import { redeemAccessCode } from '../../actions/allActions';
import { initiateModalExit } from '../../util/modalExit';
import { getProductName, getThiemeShopUrl, isCodeOnlyProduct } from '../../util/cohortUtil';
import { ExternalLink } from 'react-feather';
import Modal from '../misc/Modal';
import GreenbergProductImage from '../../../assets/images/thieme/Greenberg-Flashcards_1024x1536px_ok.jpg';
import JanisPlasticsProductImage from '../../../assets/images/thieme/Janis_Plastic-Surgery-Flashcards_1024x1536px.jpg';
import TopScoreProductImage from '../../../assets/images/thieme/Radiology_Flashcards_1024x1536px_k2.jpg';
import GastrointestimalProductImage from '../../../assets/images/thieme/Radiology-Flashcards_1024x1536px_1.jpg';
import UltrasoundProductImage from '../../../assets/images/thieme/Radiology-Flashcards_1024x1536px_2.jpg';
import NeuroradiologyProductImage from '../../../assets/images/thieme/Radiology-Flashcards_1024x1536px_3';

function ThiemeUnlocker({ deck, hide, redeemAccessCode, user, userProperties, subscriptions }) {
    const [variant, setVariant] = useState('');
    const [accessCode, setAccessCode] = useState('');
    const [thiemePurchaseExpired, setThiemePurchaseExpired] = useState(false);
    // Code-only products can't be bought in Synaptiq — codes are sold by Thieme
    const codeOnly = isCodeOnlyProduct(deck?.stripe_product_id);
    const thiemeShopUrl = getThiemeShopUrl(deck?.stripe_product_id);

    useEffect(() => {
        const productName = getProductName(deck.stripe_product_id);
        setVariant(productName);

        if (deck.stripe_product_id &&
            !subscriptions[deck.stripe_product_id] &&
            userProperties[deck.stripe_product_id] &&
            !user.admin &&
            !user.curator) {
            const purchaseDate = userProperties[deck.stripe_product_id];
            const date = moment(purchaseDate);
            const oneYearAgo = moment().subtract(1, 'years');
            const isExpired = date.isBefore(oneYearAgo);
            console.log('isExpired: ', isExpired);
            setThiemePurchaseExpired(isExpired);
        }
    }, []);

    useEffect(() => {
        document.body.classList.add("overflow-hidden");
        window.addEventListener('keydown', debouncedChangeHandler);
        return () => {
            window.removeEventListener('keydown', debouncedChangeHandler);
        }
    }, [accessCode]);

    const keyBinds = (e) => {
        if (e.key === "Enter" || e.which === 13 || e.code === "Enter") {
            if (!!accessCode) {
                handleSaveChanges();
            }
        }
    }

    const debouncedChangeHandler = useMemo(
        () => debounce(keyBinds, 50, { 'leading': true, 'trailing': false })
    , [accessCode]);

    const handleSaveChanges = () => {
        redeemAccessCode(accessCode).then(res => {
            if (res.status === 'success') {
                const toastConfig = { position: "bottom-left", autoClose: 3000, hideProgressBar: true, closeOnClick: true, pauseOnHover: true, draggable: false, progress: undefined }
                toast.success('Deck successfully unlocked!', toastConfig);
                modalExit();
            } else {
                const toastConfig = { position: "bottom-left", autoClose: false, hideProgressBar: true, closeOnClick: true, pauseOnHover: true, draggable: false, progress: undefined }
                toast.error('Access code invalid.', toastConfig);
            }
        });
        setAccessCode('');
    }

    const modalExit = () => {
        document.body.classList.remove("overflow-hidden");
        initiateModalExit('.modal--thieme-unlocker', hide);
    }

    const handleInputChange = (event) => {
        setAccessCode(event.target.value);
    };

    const renderUnlockContent = () => {
        return (
            <>
                <div className='modal_content_main_body_subleft'>
                    {variant === 'gberg'
                        && <img src={GreenbergProductImage} alt="Greenberg's Handbook of Neurosurgery Spaced-Repetition Flashcards"/>}
                    {variant === 'plastics'
                        && <img src={JanisPlasticsProductImage} alt="Essentials of Plastic Surgery | Q&A Companion, Second Edition"/>}
                    {variant === 'top_score'
                        && <img src={TopScoreProductImage} alt="Top Score for the Radiology Boards"/>}
                    {variant === 'ultrasound'
                        && <img src={UltrasoundProductImage} alt="Ultrasound Q&A Review for the Boards"/>}
                    {variant === 'neuroradiology'
                        && <img src={NeuroradiologyProductImage} alt="Neuroradiology Q&A For the Radiology Boards"/>}
                    {variant === 'gastrointestinal'
                        && <img src={GastrointestimalProductImage} alt="Gastrointestinal Imaging Q&A For the Radiology Boards"/>}
                </div>
                <div className='modal_content_main_body_subright'>
                    <h2>Unlock <span className='highlight'>{deck.name}</span></h2>
                    <div className='options'>
                        {thiemePurchaseExpired && (
                            <p className='expired-message'>
                                {codeOnly
                                    ? 'Your access to this deck has expired, but your progress has been saved. Please redeem a new access code to continue studying.'
                                    : 'Your access to this deck has expired, but your progress has been saved. Please purchase the deck again to continue studying.'}
                            </p>
                        )}
                        {!codeOnly && (
                            <>
                                <div className='input'>
                                    <label>Purchase Deck</label>
                                </div>
                                <Link to={`/checkout/${deck.stripe_product_id}`} className='button button--sub'>Unlock Now</Link>
                                <div className='or'>OR</div>
                            </>
                        )}
                        <form>
                            <div className='input'>
                                <label className='label'>Redeem a Code</label>
                                <input type="text" value={accessCode} onChange={handleInputChange} placeholder='Enter code...'/>
                                {accessCode && <button type='button' className='button button--success' onClick={handleSaveChanges}>Unlock</button>}
                            </div>
                        </form>
                        {codeOnly && thiemeShopUrl && (
                            <>
                                <div className='or'>OR</div>
                                <a
                                    href={thiemeShopUrl}
                                    className='button button--sub button--icon-right'
                                    target='_blank'
                                    rel='noopener noreferrer'>
                                    Get a Code on Thieme<ExternalLink />
                                </a>
                            </>
                        )}
                    </div>
                </div>
            </>
        )
    }

    return (
        <Modal
            name={'modal--thieme-unlocker'}
            hide={hide}
            content={renderUnlockContent()}
            cancelButtonText={'Close'}
        >
        </Modal>
    )
}

const mapStateToProps = (state) => ({
    user: state.user,
    userProperties: state.userProperties,
    subscriptions: state.subscriptions,
});
const mapDispatchToProps = (dispatch) => ({
    redeemAccessCode: (accessCode) => dispatch(redeemAccessCode(accessCode))
});
export default connect(mapStateToProps, mapDispatchToProps)(ThiemeUnlocker);
